<template>
  <div v-if="group">
    <CCard>
      <CCardHeader>
        <strong>{{ group.name }}</strong>
        <CBadge :color="group.family === 'ipv6' ? 'dark' : 'light'" class="ml-2">{{ group.family }}</CBadge>
        <CBadge :color="group.route_type === 'blackhole' ? 'danger' : 'success'" class="ml-1">{{ group.route_type }}</CBadge>
        <CBadge v-if="group.as_address_list" color="info" class="ml-1">alist: {{ group.list_name }}</CBadge>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="$router.back()">返回</CButton>
            <CButton color="success" size="sm" @click="syncAll">同步全部</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <p v-if="group.description" class="text-muted">{{ group.description }}</p>
        <CRow class="mb-3">
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">Distance</small>
              <br><strong>{{ group.distance !== null ? group.distance : '默认' }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">Routing Table</small>
              <br><strong>{{ group.routing_table }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">网段数</small>
              <br><strong>{{ group.entries.length }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">关联路由器</small>
              <br><strong>{{ group.routers.length }}</strong>
            </CCallout>
          </CCol>
        </CRow>

        <!-- 网段条目 -->
        <div class="mb-3">
          <h6>组内网段
            <CButton color="primary" size="sm" class="ml-2" @click="openEntryModal()">添加网段</CButton>
            <small class="text-muted ml-2">修改后需点「同步全部」下发</small>
          </h6>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>网段</th>
                <th width="180">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in group.entries" :key="e.id">
                <td><code>{{ e.address }}</code></td>
                <td>
                  <CButtonGroup size="sm">
                    <CButton color="info" size="sm" @click="openEntryModal(e)">编辑</CButton>
                    <CButton color="danger" size="sm" @click="deleteEntry(e)">删除</CButton>
                  </CButtonGroup>
                </td>
              </tr>
              <tr v-if="group.entries.length === 0">
                <td colspan="2" class="text-center text-muted">暂无网段</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 关联路由器（穿梭框） -->
        <div class="mb-3">
          <h6>关联路由器
            <small class="text-muted ml-2">添加关联立即下发；移除关联自动清理路由器上的组记录</small>
          </h6>
          <div class="d-flex align-items-center router-transfer">
            <div class="transfer-panel">
              <div class="transfer-header">可选路由器 ({{ availableRouters.length }})</div>
              <div class="transfer-list">
                <div
                  v-for="r in availableRouters"
                  :key="r.id"
                  class="transfer-item"
                  :class="{ active: leftSelected.includes(r.id) }"
                  @click="toggleLeftSelect(r.id)"
                >
                  {{ r.name }}
                  <small class="text-muted ml-1">{{ r.host }}</small>
                </div>
                <div v-if="availableRouters.length === 0" class="transfer-empty">全部已选</div>
              </div>
            </div>
            <div class="transfer-actions">
              <CButtonGroup vertical>
                <CButton color="primary" size="sm" :disabled="leftSelected.length === 0" @click="moveRight">添加 &raquo;</CButton>
                <CButton color="success" size="sm" :disabled="rightSelected.length === 0" @click="moveLeft">&laquo; 移除(清理)</CButton>
              </CButtonGroup>
            </div>
            <div class="transfer-panel">
              <div class="transfer-header">已关联 ({{ selectedRouterIds.length }})</div>
              <div class="transfer-list">
                <div
                  v-for="r in selectedRouters"
                  :key="r.id"
                  class="transfer-item"
                  :class="{ active: rightSelected.includes(r.id) }"
                  @click="toggleRightSelect(r.id)"
                >
                  {{ r.name }}
                  <small class="text-muted ml-1">{{ r.host }}</small>
                </div>
                <div v-if="selectedRouters.length === 0" class="transfer-empty">暂未关联</div>
              </div>
            </div>
          </div>
          <div class="mt-2" v-if="routersDirty">
            <CButtonGroup>
              <CButton color="success" size="sm" :disabled="savingRouters" @click="saveRouters">
                {{ savingRouters ? '执行中...' : '保存关联（新增立即下发/移除自动清理）' }}
              </CButton>
              <CButton color="danger" size="sm" @click="resetRouters">取消</CButton>
            </CButtonGroup>
          </div>
        </div>

        <!-- 同步结果 -->
        <div v-if="lastSyncResults.length" class="mb-3">
          <h6>最近同步结果</h6>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>路由器</th>
                <th>结果</th>
                <th>路由 +/−/set</th>
                <th v-if="group.as_address_list">AList +/−</th>
                <th width="120">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in lastSyncResults" :key="r.router_id">
                <td>{{ r.router_name }}</td>
                <td>
                  <CBadge :color="r.ok ? 'success' : 'danger'">{{ r.ok ? '成功' : '失败' }}</CBadge>
                  <small v-if="r.error" class="text-danger ml-1">{{ r.error }}</small>
                </td>
                <td v-if="r.ok">{{ r.route_added }} / {{ r.route_removed }} / {{ r.route_updated }}</td>
                <td v-else>-</td>
                <td v-if="group.as_address_list">{{ r.ok ? (r.alist_added + ' / ' + r.alist_removed) : '-' }}</td>
                <td>
                  <CButton v-if="r.ok" color="success" size="sm" @click="syncSingle(r)">重新同步</CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- 网段编辑 Modal -->
    <CModal :show.sync="entryModal.show" :title="entryModal.isEdit ? '编辑网段' : '添加网段'" color="primary">
      <CForm>
        <CInput
          label="网段"
          v-model="entryModal.form.address"
          :placeholder="group.family === 'ipv6' ? '如 2a01:d0::/32' : '如 22.0.0.0/8'"
        />
        <CAlert color="info" fade>
          网段须为 {{ group.family }} 地址。添加/修改后记得点「同步全部」。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="entryModal.show = false">取消</CButton>
          <CButton color="success" @click="submitEntry">保存</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RouteGroupDetail',
  data() {
    return {
      group: null,
      allRouters: [],
      selectedRouterIds: [],
      originalRouterIds: [],
      routersDirty: false,
      savingRouters: false,
      leftSelected: [],
      rightSelected: [],
      lastSyncResults: [],
      entryModal: {
        show: false,
        isEdit: false,
        editId: null,
        form: { address: '' }
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    availableRouters() {
      return this.allRouters.filter(r => !this.selectedRouterIds.includes(r.id))
    },
    selectedRouters() {
      return this.allRouters.filter(r => this.selectedRouterIds.includes(r.id))
    }
  },
  mounted() {
    this.loadGroup()
    this.loadAllRouters()
  },
  methods: {
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadGroup() {
      try {
        const res = await this.$axios.get(`user/route-group/${this.$route.params.id}`)
        if (res.data.code === 200) {
          this.group = res.data.data
          this.selectedRouterIds = (this.group.routers || []).map(r => r.id)
          this.originalRouterIds = [...this.selectedRouterIds]
          this.routersDirty = false
          this.leftSelected = []
          this.rightSelected = []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    toggleLeftSelect(id) {
      const i = this.leftSelected.indexOf(id)
      if (i >= 0) this.leftSelected.splice(i, 1)
      else this.leftSelected.push(id)
    },
    toggleRightSelect(id) {
      const i = this.rightSelected.indexOf(id)
      if (i >= 0) this.rightSelected.splice(i, 1)
      else this.rightSelected.push(id)
    },
    markDirty() {
      this.routersDirty = JSON.stringify(this.selectedRouterIds.sort()) !== JSON.stringify(this.originalRouterIds.sort())
    },
    moveRight() {
      this.selectedRouterIds.push(...this.leftSelected)
      this.leftSelected = []
      this.markDirty()
    },
    moveLeft() {
      const rm = this.rightSelected
      this.rightSelected = []
      this.selectedRouterIds = this.selectedRouterIds.filter(id => !rm.includes(id))
      this.markDirty()
    },
    resetRouters() {
      this.selectedRouterIds = [...this.originalRouterIds]
      this.routersDirty = false
      this.leftSelected = []
      this.rightSelected = []
    },
    async saveRouters() {
      this.savingRouters = true
      try {
        const res = await this.$axios.post(`user/route-group/${this.group.id}/routers`, {
          router_ids: this.selectedRouterIds
        })
        const results = (res.data.data || {}).results || []
        if (results.length > 0) {
          this.lastSyncResults = results.map(r => ({
            router_id: r.router_id,
            router_name: r.router_name || '?',
            ok: r.ok,
            error: r.error,
            route_added: '-', route_removed: '-', route_updated: '-',
            alist_added: '-', alist_removed: '-'
          }))
        }
        const failed = results.filter(r => !r.ok)
        if (res.data.code === 200 && failed.length === 0) {
          this.$toasted.show('关联已保存', { type: 'success' })
        } else {
          this.$toasted.show(`部分失败：${failed.length} 台`, { type: 'warning' })
        }
        await this.loadGroup()
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.savingRouters = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    async syncAll() {
      this.showConfirm('确认同步', `确认同步到 ${this.selectedRouterIds.length} 台路由器？`, async () => {
        try {
          const res = await this.$axios.post(`user/route-group/${this.group.id}/sync`)
          this.lastSyncResults = (res.data.data || {}).results || []
          const failed = this.lastSyncResults.filter(r => !r.ok)
          this.$toasted.show(
            failed.length > 0 ? `同步完成，${failed.length} 台失败` : '同步完成',
            { type: failed.length > 0 ? 'warning' : 'success' }
          )
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    async syncSingle(r) {
      try {
        const res = await this.$axios.post(`user/route-group/${this.group.id}/sync/${r.router_id}`)
        const results = (res.data.data || {}).results || []
        if (results.length > 0) {
          this.lastSyncResults = this.lastSyncResults.map(x =>
            x.router_id === r.router_id ? results[0] : x
          )
        }
        this.$toasted.show('已同步 ' + r.router_name, { type: 'success' })
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    openEntryModal(e) {
      if (e) {
        this.entryModal.isEdit = true
        this.entryModal.editId = e.id
        this.entryModal.form.address = e.address
      } else {
        this.entryModal.isEdit = false
        this.entryModal.editId = null
        this.entryModal.form.address = ''
      }
      this.entryModal.show = true
    },
    async submitEntry() {
      const f = this.entryModal.form
      if (!f.address) {
        this.$toasted.show('请填写网段', { type: 'error' })
        return
      }
      try {
        let res
        if (this.entryModal.isEdit) {
          res = await this.$axios.post(`user/route-group/entry/${this.entryModal.editId}/edit`, f)
        } else {
          res = await this.$axios.post(`user/route-group/${this.group.id}/entry/add`, f)
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存，记得同步', { type: 'success' })
          this.entryModal.show = false
          this.loadGroup()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    deleteEntry(e) {
      this.showConfirm('确认删除', `确认删除网段 ${e.address}？删除后需同步才会从路由器移除。`, async () => {
        try {
          const res = await this.$axios.delete(`user/route-group/entry/${e.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除，记得同步', { type: 'success' })
            this.loadGroup()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e2) {
          this.$toasted.show('网络错误: ' + e2.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.router-transfer .transfer-panel {
  flex: 1;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  max-height: 300px;
  display: flex;
  flex-direction: column;
}
.transfer-header {
  padding: 6px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 13px;
}
.transfer-list {
  overflow-y: auto;
  flex: 1;
}
.transfer-item {
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
  font-size: 13px;
}
.transfer-item:hover { background: #f1f3f5; }
.transfer-item.active { background: #c8e6f8; }
.transfer-empty {
  padding: 20px;
  text-align: center;
  color: #868e96;
}
.transfer-actions {
  padding: 0 8px;
}
</style>
